<template>
  <main-layout
    controlsBg="light"
    :hasControlsBar="false"
    :breadcrumb="getBreadcrumb">
    <div class="container">
      <grid-row column-align="center">
        <grid-col col-size="4">
          <h1 class="header margin-top-xl">
            <chevron
              @click="$router.push({name: 'IngestTemplates'})"/>
            {{ $t('health.nutritionalPlan.editTemplate') }}
          </h1>
          <alert
            v-if="alertVisible"
            :message="alertMessage"
            :type="alertType">
          </alert>
        </grid-col>
      </grid-row>
      <form
        v-on:submit.prevent
        v-on:keypress.enter.prevent="onSave">
        <grid-row column-align="center">
          <grid-col col-size="3">
            <div class="variable-input-field variable-input-field-name">
              <label-input
                ref="nameInput"
                form-size="m"
                :label="$t('health.nutritionalPlan.name')"
                v-model="name"
                :type="'text'"
                :error="nameError"
                :errorMessage="displayNameErrorMessage">
              </label-input>
            </div>
          </grid-col>

          <grid-col col-size="1">
            <div class="center-vertical variable-input-field submit-buttons align-right">
              <btn-pill
                :btnText="$t('health.userProfile.update')"
                v-on:click="onSave"
                btn-size="m">
              </btn-pill>
            </div>
          </grid-col>
        </grid-row>

        <grid-row column-align="center">
            <grid-col col-size="1">
              <div class="variable-input-field variable-input-field-name">
                <label-input
                  ref="carbsInput"
                  form-size="s"
                  :label="$t('health.nutritionalPlan.carbsFactor')"
                  v-model="carbsFactor"
                  :type="'Number'"
                  :step="'any'"
                  :error="carbsFactorError"
                  :errorMessage="displayMacrosErrorMessage">
                </label-input>
              </div>
            </grid-col>

          <grid-col col-size="1">
            <div class="variable-input-field variable-input-field-name">
              <label-input
                ref="proteinsInput"
                form-size="s"
                :label="$t('health.nutritionalPlan.proteinsFactor')"
                v-model="proteinsFactor"
                :type="'Number'"
                :step="'any'"
                :error="proteinsFactorError"
                :errorMessage="displayMacrosErrorMessage">
              </label-input>
            </div>
          </grid-col>

          <grid-col col-size="1">
            <div class="variable-input-field variable-input-field-name">
              <label-input
                ref="fatsInput"
                form-size="s"
                :label="$t('health.nutritionalPlan.fatsFactor')"
                v-model="fatsFactor"
                :type="'Number'"
                :step="'any'"
                :error="fatsFactorError"
                :errorMessage="displayMacrosErrorMessage">
              </label-input>
            </div>
          </grid-col>

          <grid-col col-size="1">
            <div class="variable-input-field variable-input-field-name">
              <label-input
                ref="caloriesInput"
                form-size="s"
                :label="$t('health.nutritionalPlan.caloriesFactor')"
                v-model="caloriesFactor"
                :type="'Number'"
                :step="'any'"
                :error="caloriesFactorError"
                :errorMessage="displayMacrosErrorMessage">
              </label-input>
            </div>
          </grid-col>
        </grid-row>

        <grid-row column-align="center">
          <grid-col col-size="2">
                <span
                  class="margin-right-m">
                  {{ $t('health.nutritionalPlan.type') }}
                </span>
            <v-select class="v-select--light"
                      :value="type" :onChange="changeType" :searchable="true"
                      :options="$t('health.nutritionalPlan.mealTypes')"></v-select>
          </grid-col>

          <grid-col col-size="1">
          </grid-col>

          <grid-col col-size="1">
                  <span
                    class="align-right"
                    v-tooltip.left.top-start="$t('health.nutritionalPlan.addFood')"
                    @click="showFoodModal = true"
                  >
                    <font-awesome-icon
                      class="header-add-button-icon center-vertical"
                      :icon="icon.add"/>
                  </span>
          </grid-col>
        </grid-row>
        <grid-row column-align="center">
          <grid-col col-size="4">
            <p class="error margin-top-s" v-if="typeError">
              <font-awesome-icon
                class="error"
                :icon="icon.info"/>
              {{$t('health.nutritionalPlan.typeNotSelected')}}
              </p>
          </grid-col>
        </grid-row>
        <grid-row column-align="center">
          <grid-col col-size="4">
            <food-table
              ref="foodTable"
              :items="items"
              :noWeight="noWeight"
              v-on:delete="deleteFood"
              v-on:updateAmount="updateFoodAmount"
              v-on:updateRecalculate="updateRecalculate"
            />
          </grid-col>
        </grid-row>
        <grid-row class="margin-top-l" column-align="center">
            <grid-col col-size="4">
              <total-macros-table
                ref="totalMacrosTable"
                :items="totalsItems"
              />
            </grid-col>
        </grid-row>

      </form>
      <add-food-modal
        v-if="showFoodModal"
        v-model="food"
        :foods="getFilteredFoods"
        v-on:close="closeFoodModal"
        v-on:input="addFood"
      />

      <ok-cancel-modal
        v-if="showConfirmationDeleteModal"
        v-on:ok="onDeleteConfirm"
        v-on:cancel="onDeleteCancel"
        :title="deleteModalMessage"
        :message="deleteModalMessage"
        :okButton="$t('commons.delete')"
        :cancelButton="$t('commons.cancel')">
        <div slot="body">
          <ul>
            <li>
              {{ foodToDelete.food_name }}
            </li>
          </ul>
        </div>
      </ok-cancel-modal>
    </div>
  </main-layout>
</template>

<script>

import MainLayout from '@/layouts/MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import Alert from '@/components/shared/alert/Alert';
import Chevron from '@/components/shared/Chevron';
import LabelInput from '@/components/shared/forms/LabelInput';
import vSelect from 'vue-select';
import {faPlusCircle} from '@fortawesome/pro-solid-svg-icons';
import {faInfoCircle} from '@fortawesome/fontawesome-free-solid';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import AddPlanModal from 'BRAND/components/nutritionPlan/AddPlanModal';
import AddFoodModal from 'BRAND/components/nutritionPlan/AddFoodModal';
import api from 'BRAND/api/nutritionPlan';
import BtnPill from '@/components/shared/buttons/BtnPill';
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';
import FoodTable from 'BRAND/components/nutritionPlan/FoodTable';
import TotalMacrosTable from 'BRAND/components/nutritionPlan/TotalMacrosTable';
import {mapGetters} from 'vuex';

export default {
  name: 'Template',
  components: {
    AddFoodModal,
    MainLayout,
    GridCol,
    GridRow,
    Alert,
    Chevron,
    LabelInput,
    vSelect,
    FontAwesomeIcon,
    AddPlanModal,
    BtnPill,
    OkCancelModal,
    FoodTable,
    TotalMacrosTable
  },
  data () {
    return {
      plan: {},
      food: {},
      items: [],
      totalsItems: [],
      noWeight: true,
      weight: null,
      foods: undefined,
      foodToDelete: undefined,
      nameError: false,
      nameErrorUnique: false,
      name: '',
      type: '',
      carbsFactor: '0',
      proteinsFactor: '0',
      fatsFactor: '0',
      caloriesFactor: '0',
      carbsFactorError: false,
      proteinsFactorError: false,
      fatsFactorError: false,
      caloriesFactorError: false,
      typeError: false,
      showFoodModal: false,

      showConfirmationDeleteModal: false,
      deleteModalMessage: '',
      /* UI controls */
      alertVisible: false,
      alertMessage: '',
      icon: {
        add: faPlusCircle,
        info: faInfoCircle
      },
      alertType: '', // error, warning, success'
      mealTypes: [
        'Desayuno',
        'Almuerzo',
        'Comida',
        'Merienda',
        'Cena'
      ],
      theoreticalTotal: {
        id: 0,
        name: this.$t('health.nutritionalPlan.theoreticalTotal'),
        carbs: 0,
        proteins: 0,
        fats: 0,
        calories: 0
      },
      realTotal: {
        id: 1,
        name: this.$t('health.nutritionalPlan.realTotal'),
        carbs: 0,
        proteins: 0,
        fats: 0,
        calories: 0
      }
    };
  },
  watch: {
    carbsFactor: {
      handler: function () {
        this.theoreticalTotal.carbs = this.weight * parseFloat(this.carbsFactor);
        this.updateTheoretical();
      }
    },
    proteinsFactor: {
      handler: function () {
        this.theoreticalTotal.proteins = this.weight * parseFloat(this.proteinsFactor);
        this.updateTheoretical();
      }
    },
    fatsFactor: {
      handler: function () {
        this.theoreticalTotal.fats = this.weight * parseFloat(this.fatsFactor);
        this.updateTheoretical();
      }
    },
    caloriesFactor: {
      handler: function () {
        this.theoreticalTotal.calories = this.weight * parseFloat(this.caloriesFactor);
        this.updateTheoretical();
      }
    },
    items: {
      handler: function () {
        this.realTotal.carbs = 0;
        this.realTotal.proteins = 0;
        this.realTotal.fats = 0;
        this.realTotal.calories = 0;
        this.items.forEach(value => {
          this.realTotal.carbs += value.carbs;
          this.realTotal.proteins += value.protein;
          this.realTotal.fats += value.fat;
          this.realTotal.calories += value.calories;
        });
        this.realTotal.carbs = Math.round(this.realTotal.carbs * 100) / 100;
        this.realTotal.proteins = Math.round(this.realTotal.proteins * 100) / 100;
        this.realTotal.fats = Math.round(this.realTotal.fats * 100) / 100;
        this.realTotal.calories = Math.round(this.realTotal.calories * 100) / 100;
        this.totalsItems = this.totalsItems.filter(value => value.id !== this.realTotal.id);
        this.totalsItems.push(this.realTotal);
      }
    }
  },
  created () {
    if (this.$route.params.mode === undefined) {
      this.$router.push({name: 'IngestTemplates'});
    }
  },
  mounted () {
    this.totalsItems = [];
    this.totalsItems.push(this.realTotal);
    if (this.$route.params.mode === 'edit') {
      this.plan = this.$route.params.plan;
      this.name = this.plan.name;
      this.type = this.plan.type;
      this.items = this.plan.items;
      this.carbsFactor = this.plan.carbsFactor.toString();
      this.proteinsFactor = this.plan.proteinsFactor.toString();
      this.fatsFactor = this.plan.fatsFactor.toString();
      this.caloriesFactor = this.plan.caloriesFactor.toString();
      const typeIndex = this.mealTypes.findIndex((e) => e === this.plan.type);
      this.type = this.$t('health.nutritionalPlan.mealTypes')[typeIndex];
    } else {
      this.plan = {
        'template': true,
        'items': []
      };
    }
    const languageCode = this.getLanguageCode;
    api.getFoodList(this, languageCode).then(data => {
      this.foods = data;
    });
  },
  computed: {
    ...mapGetters([
      'getLanguageCode'
    ]),
    getBreadcrumb () {
      return [
        {
          name: this.$t('menu.workspace'),
          link: '/workspace'
        },
        {
          name: this.$t('health.nutritionalPlan.menu'),
          link: '/nutritionalPlan/menu'
        },
        {
          name: this.$t('health.nutritionalPlan.templates'),
          link: '/ingest/templates'
        },
        {
          name: this.$t('health.nutritionalPlan.editTemplate'),
          link: '/ingest/template',
          disabled: true
        }
      ];
    },
    displayNameErrorMessage () {
      let errorMessage = this.$t('commons.mandatoryFieldMessage');
      if (this.nameErrorUnique) {
        errorMessage = this.$t('health.nutritionalPlan.templateNameConflict');
      }
      return errorMessage;
    },
    displayMacrosErrorMessage () {
      return this.$t('health.nutritionalPlan.negativeFactors');
    },
    getFilteredFoods () {
      return this.foods ? this.foods.filter(food => !this.items.some(item => item.food_id === food.food_id)) : [];
    }
  },
  methods: {
    onSave () {
      clearErrors(this);
      if (isValidForm(this)) {
        this.plan.name = this.name;
        this.plan.carbsFactor = parseFloat(this.carbsFactor);
        this.plan.fatsFactor = parseFloat(this.fatsFactor);
        this.plan.caloriesFactor = parseFloat(this.caloriesFactor);
        this.plan.proteinsFactor = parseFloat(this.proteinsFactor);
        let call;
        if (this.plan.id === undefined || this.plan.id === -1) {
          call = api.createPlan(this, this.plan).then(data => {
            this.plan.id = data;
          });
        } else {
          call = api.updatePlan(this, this.plan);
        }
        call.then(() => {
          showAlert(this, this.$t('health.nutritionalPlan.successUpdate'), 'success');
        }).catch((error) => {
          if (error.status === 409) {
            this.nameError = true;
            this.nameErrorUnique = true;
          } else {
            showAlert(this, this.$t('health.nutritionalPlan.errorUpdate'), 'error');
          }
        });
      }
    },
    changeType (type) {
      if (type === null) {
        type = this.$t('health.nutritionalPlan.mealTypes')[0];
      }
      const typeIndex = this.$t('health.nutritionalPlan.mealTypes').findIndex((e) => e === type);
      this.plan.type = this.mealTypes[typeIndex];
    },
    deleteFood (food) {
      this.foodToDelete = food;
      this.deleteModalMessage = this.$t('health.nutritionalPlan.deleteFood');
      this.showConfirmationDeleteModal = true;
    },
    updateFoodAmount (food) {
      api.getFoodInfo(this, food.food_id).then((data) => {
        const newItem = {
          'amount': food.amount,
          'food_id': food.food_id,
          'food_name': food.food_name,
          'calories': Math.round(((data.energy_kJ_value * food.amount) / (4.184 * data.portion)) * 100) / 100,
          'protein': Math.round(((data.protein_grams_value * food.amount) / data.portion) * 100) / 100,
          'fat': Math.round(((data.fat_grams_value * food.amount) / data.portion) * 100) / 100,
          'carbs': Math.round(((data.carbohydrate_gram_value * food.amount) / data.portion) * 100) / 100,
          'caloriesWeight': Math.round(((data.energy_kJ_value * food.amount) / (4.184 * this.weight * data.portion)) * 100) / 100,
          'carbsWeight': Math.round(((data.carbohydrate_gram_value * food.amount) / (this.weight * data.portion)) * 100) / 100,
          'proteinWeight': Math.round(((data.protein_grams_value * food.amount) / (this.weight * data.portion)) * 100) / 100,
          'fatWeight': Math.round(((data.fat_grams_value * food.amount) / (this.weight * data.portion)) * 100) / 100,
          'recalculate': food.recalculate
        };
        this.plan.items = this.plan.items.filter(item => item.food_id !== food.food_id);
        this.plan.items.push(newItem);
        this.items = this.plan.items;
      });
    },
    updateRecalculate (food) {
      this.plan.items = this.plan.items.map(item => {
        if (item.food_id === food.food_id) {
          item.recalculate = !item.recalculate;
        }
        return item;
      });
      this.items = this.plan.items;
    },
    closeFoodModal () {
      this.showFoodModal = false;
      this.food = {};
    },
    addFood (food) {
      this.food = food;

      api.getFoodInfo(this, this.food.food_id).then((data) => {
        const toAdd = {
          'food_id': food.food_id,
          'food_name': food.food_name,
          'amount': data.portion,
          'calories': Math.round((data.energy_kJ_value / 4.184) * 100) / 100,
          'protein': data.protein_grams_value,
          'fat': data.fat_grams_value,
          'carbs': data.carbohydrate_gram_value,
          'caloriesWeight': Math.round((data.energy_kJ_value / (4.184 * this.weight)) * 100) / 100,
          'carbsWeight': Math.round((data.carbohydrate_gram_value / this.weight) * 100) / 100,
          'proteinWeight': Math.round((data.protein_grams_value / this.weight) * 100) / 100,
          'fatWeight': Math.round((data.fat_grams_value / this.weight) * 100) / 100,
          'recalculate': true
        };
        this.plan.items.push(toAdd);
        this.items = this.plan.items;
      }).catch(() => {
        showAlert(this, this.$t('health.nutritionalPlan.errorAddFood'), 'error');
      });
    },
    onDeleteConfirm () {
      this.showConfirmationDeleteModal = false;
      this.plan.items = this.plan.items.filter(value => value.food_id !== this.foodToDelete.food_id);
      this.foodToDelete = {};
      this.items = this.plan.items;
    },
    onDeleteCancel () {
      this.foodToDelete = {};
      this.showConfirmationDeleteModal = false;
    },
    updateTheoretical () {
      this.totalsItems = this.totalsItems.filter(value => value.id !== this.theoreticalTotal.id);
    },
    adjustAmounts () {
      clearErrors(this);
      if (this.carbsFactor <= 0) {
        this.carbsFactorError = true;
        return;
      }
      const foodsToRecalculate = this.items.filter(value => value.recalculate);
      const foodsWithPermanentAmount = this.items.filter(value => !value.recalculate);
      const permanentFoodCarbs = foodsWithPermanentAmount.reduce((previousValue, currentValue) => previousValue + currentValue.carbs, 0);
      const currentRecalculateFoodCarbs = foodsToRecalculate.reduce((previousValue, currentValue) => previousValue + currentValue.carbs, 0);
      const theoreticalCarbs = this.weight * this.carbsFactor - permanentFoodCarbs;
      if (theoreticalCarbs < permanentFoodCarbs) {
        showAlert(this, this.$t('health.nutritionalPlan.errorExceededCarbs'), 'error');
        return;
      }
      const recalculationNeededCarbs = theoreticalCarbs - currentRecalculateFoodCarbs;
      foodsToRecalculate.forEach(value => {
        const percentageCarbs = Math.abs(value.carbs / currentRecalculateFoodCarbs);
        const toAddCarbsAmount = percentageCarbs * recalculationNeededCarbs;
        const totalCarbs = value.carbs + toAddCarbsAmount;
        const newAmount = Math.round(value.amount * totalCarbs / value.carbs);
        api.getFoodInfo(this, value.food_id).then((data) => {
          const newItem = {
            'amount': newAmount,
            'food_id': value.food_id,
            'food_name': value.food_name,
            'calories': Math.round(((data.energy_kJ_value * newAmount) / (4.184 * data.portion)) * 100) / 100,
            'protein': Math.round(((data.protein_grams_value * newAmount) / data.portion) * 100) / 100,
            'fat': Math.round(((data.fat_grams_value * newAmount) / data.portion) * 100) / 100,
            'carbs': Math.round(((data.carbohydrate_gram_value * newAmount) / data.portion) * 100) / 100,
            'caloriesWeight': Math.round(((data.energy_kJ_value * newAmount) / (4.184 * this.weight * data.portion)) * 100) / 100,
            'carbsWeight': Math.round(((data.carbohydrate_gram_value * newAmount) / (this.weight * data.portion)) * 100) / 100,
            'proteinWeight': Math.round(((data.protein_grams_value * newAmount) / (this.weight * data.portion)) * 100) / 100,
            'fatWeight': Math.round(((data.fat_grams_value * newAmount) / (this.weight * data.portion)) * 100) / 100,
            'recalculate': value.recalculate
          };
          this.plan.items = this.plan.items.filter(item => item.food_id !== value.food_id);
          this.plan.items.push(newItem);
          this.items = this.plan.items;
        });
      });
    }
  }
};

const isValidForm = (context) => {
  let valid = true;
  if (!(context.name && context.name.length > 0)) {
    valid = false;
    context.nameError = true;
  }
  if (context.carbsFactor <= 0) {
    valid = false;
    context.carbsFactorError = true;
  }
  if (context.proteinsFactor < 0) {
    valid = false;
    context.proteinsFactorError = true;
  }
  if (context.fatsFactor < 0) {
    valid = false;
    context.fatsFactorError = true;
  }
  if (context.caloriesFactor < 0) {
    valid = false;
    context.caloriesFactorError = true;
  }
  if (!context.mealTypes.includes(context.plan.type)) {
    valid = false;
    context.typeError = true;
  }

  return valid;
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 3000);
};

const clearErrors = (context) => {
  context.nameError = false;
  context.nameErrorUnique = false;
  context.carbsFactorError = false;
  context.proteinsFactorError = false;
  context.fatsFactorError = false;
  context.caloriesFactorError = false;
  context.typeError = false;
};

</script>

<style lang="scss" scoped>
@import "../../../../stylesheets/variables";

.header-add-button-icon {
  font-size: 3rem;
  color: $secondary;
}

.align-right {
  float: right;
  clear: both;
}

.variable-input-field {
  display: inline-block;
}

.submit-buttons {
  float: right;
}

.center-vertical {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}
.error {
  color: #e63939;
}

</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/nutritionalPlan/Template.vue